<template>
  <div class="ts-space-y-3">
    <div class="row">
      <div class="col-md-4">
        <div class="ts-flex ts-justify-between ts-mb-1">
          <label class="required">{{
            $t('studentScoringEntry.mainProgram')
          }}</label>
        </div>
        <a-select
          v-model="model.acp_id"
          style="width: 100%"
          :options="mainPrograms"
          :showSearch="true"
          :filterOption="
            (input, option) =>
              option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
          "
        ></a-select>
        <div class="ts-text-red-600" v-if="errors.has('acp_id')">
          {{ errors.first('acp_id') }}
        </div>
      </div>
      <div class="col-md-8">
        <div class="ts-flex ts-justify-between ts-mb-1">
          <label class="required ts-mb-0">{{
            $t('studentScoringEntry.academicClass')
          }}</label>
          <a-radio-group v-model="acStatusId" size="small" @change="clearInput">
            <a-radio-button :value="1">
              {{ $t('studentScoringEntry.current') }}
            </a-radio-button>
            <a-radio-button :value="2">
              {{ $t('studentScoringEntry.history') }}
            </a-radio-button>
          </a-radio-group>
        </div>
        <a-select
          v-model="model.class_id"
          style="width: 100%"
          :options="
            orderByClass(classes.filter(el => el.acp_id == model.acp_id), [
              'label'
            ])
          "
          :showSearch="true"
          :filterOption="
            (input, option) =>
              option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
          "
          @change="onClassChange"
          @search="fetchClass"
        ></a-select>
        <div class="ts-text-red-600" v-if="errors.has('class_id')">
          {{ errors.first('class_id') }}
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <label class="required ts-mb-0">{{
          $t('studentScoringEntry.scoringCycle')
        }}</label>
        <a-select
          v-model="model.scoring_cycle_id"
          style="width: 100%"
          :options="scoringCycle"
          @change="scoringCycleChange"
        ></a-select>
        <div class="ts-text-red-600" v-if="errors.has('scoring_cycle_id')">
          {{ errors.first('scoring_cycle_id') }}
        </div>
      </div>
      <div class="col-md-4">
        <div v-if="model.scoring_cycle_id != 4">
          <label class="required ts-mb-0">{{
            $t('studentScoringEntry.scoringCycleValue')
          }}</label>

          <select
            style="width: 100%"
            v-model.number="model.cycle_value"
            v-if="includeValue([1, 4, 5, 7])"
            class="form-control"
            @change="onCycleValue"
          >
            <option :value="null" disabled selected style="display: none">
              {{ $t('select') }}
            </option>
            <option
              v-for="(s, index) in scoringCycleValuesList"
              :key="index"
              :value="s.scoring_cycle_value"
            >
              {{ s.scoring_cycle_description }}
            </option>
          </select>
          <input
            v-model.number="model.cycle_value"
            type="number"
            style="width: 100%"
            class="form-control"
            readonly
            v-if="includeValue([0, 2, 3])"
          />
          <input
            type="number"
            class="form-control"
            style="width: 100%"
            readonly
            v-if="model.scoring_cycle_id == null"
          />
          <div class="ts-text-red-600" v-if="errors.has('cycle_value')">
            {{ errors.first('cycle_value') }}
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <label class="ts-mb-0">{{ $t('studentScoringEntry.subject') }}</label>
        <a-select
          v-model="selectedSubjectId.id"
          style="width: 100%"
          :options="subjects"
          mode="multiple"
          :maxTagCount="2"
        ></a-select>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import {
  formViewModel,
  getScoringCycle,
  getScoringCycleValue
} from '@/api/student-scoring-table-entry'
import { debounce, orderBy } from 'lodash'
export default {
  props: ['value', 'selectedSubjectId'],
  data() {
    return {
      errors: new Errors(),
      subjects: [],
      acStatusId: 1,
      classes: [],
      mainPrograms: [],
      scoringCycle: [],
      scoringCycleValues: [],
      term_order: null,
      model: {}
    }
  },
  created() {
    this.model = this.value
    this.fetchScoringCycleValue()
  },
  mounted() {
    this.fetchClass(' ')
  },
  computed: {
    includeValue: vm => arrayValue => {
      if (arrayValue == undefined) {
        return false
      }
      return arrayValue.includes(vm.model.scoring_cycle_id)
    },
    scoringCycleValuesList() {
      if (this.model.scoring_cycle_id) {
        if (this.model.scoring_cycle_id == 7) {
          if (this.term_order == 1) {
            return this.scoringCycleValues
              .filter(s => s.scoring_cycle_id == this.model.scoring_cycle_id)
              .filter(s => _.includes([1, 2], s.scoring_cycle_value)) // Term 1, Term 2 only for semester 1
          }
          return this.scoringCycleValues
            .filter(s => s.scoring_cycle_id == this.model.scoring_cycle_id)
            .filter(s => _.includes([3, 4, 5], s.scoring_cycle_value)) // Term 3, Term 4 and Final Exam only for semester 2
        }
        return this.scoringCycleValues.filter(
          s => s.scoring_cycle_id == this.model.scoring_cycle_id
        )
      }
      return this.scoringCycleValues
    }
  },
  methods: {
    fetchScoringCycleValue() {
      this.$emit('loading', true)
      getScoringCycleValue()
        .then(response => {
          this.scoringCycleValues = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.$emit('loading', false)
        })
    },
    fetchClass: debounce(function(value) {
      if (!value) return
      this.$emit('loading', true)
      formViewModel({
        params: {
          fnName: 'academicClass,mainProgram',
          searchClass: value,
          acStatusId: this.acStatusId
        }
      })
        .then(({ data }) => {
          this.classes = data.academicClass.map(el => ({
            value: el.class_id,
            sch_id: el.sch_id,
            label: `${el.ac_year} - ${el.term_name} -${el.class_name} - ${el.class_desc} - ${el.teacher_name}`,
            acps_id: el.acps_id,
            acp_id: el.acp_id,
            ac_year_id: el.ac_year_id,
            ac_term_id: el.ac_term_id,
            term_order: el.term_order,
            grade_id: el.grade_id,
            score_type: el.score_type, // 1 numbering, 2 grading, 3 rating
            monthly_score_sc_bas_id: el.monthly_score_sc_bas_id, // 1 item base, 2 subject base
            semester_score_sc_bas_id: el.semester_score_sc_bas_id, // 1 item base, 2 subject base
            ac_category_id: el.ac_category_id // 1 សិក្សាសង្គម, 0 Normal, 2 Sciense
          }))
          this.mainPrograms = data.mainProgram.map(el => ({
            value: el.acp_id,
            label: el.acp_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.$emit('loading', false)
        })
    }, 800),
    async onClassChange(value, option) {
      this.$emit('clearEntities', [])
      this.model.sc_item_subject_comp_list = []
      const {
        acp_id,
        acps_id,
        ac_year_id,
        ac_term_id,
        term_order,
        score_type,
        ac_category_id,
        grade_id
      } = option.data.props
      this.model.acp_id = acp_id
      this.model.acps_id = acps_id
      this.model.ac_year_id = ac_year_id
      this.model.ac_term_id = ac_term_id
      this.model.scoring_cycle_id = null
      this.model.cycle_value = null
      this.model.grade_id = grade_id
      this.model.score_type = parseInt(score_type)
      this.model.ac_category_id = ac_category_id
      this.term_order = term_order
      await this.fetchScoringCyle({ acps_id: acps_id })
    },
    fetchScoringCyle({ acps_id }) {
      this.$emit('loading', true)
      return getScoringCycle({ params: { acps_id } })
        .then(response => {
          this.scoringCycle = response.data.map(el => ({
            value: el.scoring_cycle_id,
            label: el.scoring_cycle_name
          }))
          this.$emit('fetchStudent')
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.$emit('loading', false)
        })
    },
    scoringCycleChange() {
      const findClass = this.classes.find(c => c.value === this.model.class_id)
      if (this.model.scoring_cycle_id == 1) {
        this.model.score_sc_bas_id = parseInt(findClass.monthly_score_sc_bas_id)
      } else {
        this.model.score_sc_bas_id = parseInt(
          findClass.semester_score_sc_bas_id
        )
      }
      if ([0, 2, 3].includes(this.model.scoring_cycle_id)) {
        this.model.cycle_value = this.term_order
      } else if (this.model.scoring_cycle_id == 4) {
        this.model.cycle_value = 1
      } else {
        this.model.cycle_value = null
      }
      this.fetchEntities()
    },
    fetchEntities() {
      this.$emit('loading', true)
      return formViewModel({
        params: {
          fnName: 'scoreEntities',
          class_id: this.model.class_id,
          score_type: this.model.score_type
        }
      })
        .then(({ data }) => {
          if (data.scoreEntities !== null) {
            this.subjects = data.scoreEntities.entities.map(el => ({
              value: el.id,
              label: el.name_native + '-' + el.name
            }))

            this.selectedSubjectId.id.push(...this.subjects.map(el => el.value))

            this.$emit('scoreEntities', data.scoreEntities.entities)
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.$emit('loading', false)
        })
    },
    clearInput() {
      this.classes = []
      this.selectedSubjectId.id = []
      this.scoringCycle = []
      this.students = []
      this.term_order = null
      this.model.class_id = null
      this.model.grade_id = null
      this.model.scoring_cycle_id = null
      this.model.score_type = null
      this.model.acps_id = null
      this.model.cycle_value = null
      this.model.ac_year_id = null
      this.model.ac_term_id = null
      this.model.program_structure_id = null
      this.model.sc_item_subject_comp_list = []
      this.fetchClass(' ')
    },
    onCycleValue() {
      this.$emit('on-cycle-value-change')
    },
    orderByClass(classes) {
      return orderBy(classes, ['ac_term_id'])
    }
  }
}
</script>
